<script setup>
  import { computed, ref, watch, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import PageCarouselForm from './PageCarouselForm.vue';
  import useSortableList from '../composables/useSortableList.js';

  const { t } = useI18n();

  const props = defineProps({
    cohort_page_id: {
      type: Number,
      required: false
    }
  });

  const page_carousels = ref([]);
  const currentEditingPageCarouselId = ref(null);
  const pageCarouselsList = ref(null);

  const loadPageCarousels = async () => {
    currentEditingPageCarouselId.value = null; // Reset the current editing page carousel when loading page carousels
    try {
      const res = await fetch(`/admin/cohort_pages/${props.cohort_page_id}/page_carousels`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      page_carousels.value = (await res.json()).page_carousels
    } catch (error) {
      console.error('Error fetching page carousels:', error);
      page_carousels.value = [];
    }
  };

  onMounted(async () => {
    loadPageCarousels();
  });

  const move = async (pageCarouselId, position) => {
    try {
      const res = await fetch(`/admin/cohort_pages/${props.cohort_page_id}/page_carousels/${pageCarouselId}/move`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ position })
      });
      if (!res.ok) throw new Error(res.statusText);
    } catch (error) {
      console.error('Error moving page carousel:', error);
      await loadPageCarousels();
    }
  };

  useSortableList(pageCarouselsList, page_carousels, '.carousel-drag-handle', (pageCarousel, position) => move(pageCarousel.id, position));

  const newPageCarousel = () => {
    currentEditingPageCarouselId.value = 0;
  };

  const editPageCarousel = (pageCarouselId) => {
    currentEditingPageCarouselId.value = pageCarouselId;
  };

  const deletePageCarousel = async (pageCarouselId) => {
    if (confirm(t('cohort_page_carousels.delete_confirm'))) {
      try {
        await fetch(`/admin/cohort_pages/${props.cohort_page_id}/page_carousels/${pageCarouselId}.json`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        });
        await loadPageCarousels();
      } catch (error) {
        console.error('Error deleting page carousel:', error);
      }
    }
  };

  const timeTooltip = (page_carousel) => {
    if (page_carousel.start_date || page_carousel.end_date) {
      let tooltip = '';
      if (page_carousel.start_date) {
        tooltip += t('cohort_page_carousels.starts_on', { date: page_carousel.start_date });
      }
      if (page_carousel.end_date) {
        if (tooltip) tooltip += '<br>';
        tooltip += t('cohort_page_carousels.ends_on', { date: page_carousel.end_date });
      }
      return tooltip;
    }
    return '';
  };

  const adjustDate = (dateString) => {
    var date = new Date(dateString);
    var userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset * Math.sign(userTimezoneOffset));
  };

  const timeClass = (page_carousel) => {
    if (page_carousel.start_date && adjustDate(page_carousel.start_date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)) {
      return 'text-danger';
    }
    if (page_carousel.end_date && adjustDate(page_carousel.end_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      return 'text-danger';
    }
    return 'text-success';
  };
</script>

<template>
  <div class="row">
    <div class="col">
      <label for="carousels" class="form-label mt-1">{{ t('cohort_page_carousels.carousels') }}</label>

      <div class="alert alert-primary" role="alert" v-if="page_carousels.length === 0 && currentEditingPageCarouselId == null">
        {{ t('cohort_page_carousels.no_carousels') }}
      </div>

      <div class="row mb-2" v-if="currentEditingPageCarouselId != 0">
        <div class="col">
          <button class="btn btn-sm btn-primary ms-0" @click="newPageCarousel">{{ t('cohort_page_carousels.new_carousel') }}</button>
        </div>
      </div>
      <div  v-if="currentEditingPageCarouselId == 0" class="border rounded mb-2">
        <PageCarouselForm v-model="currentEditingPageCarouselId" @saved="loadPageCarousels" :cohort_page_id="props.cohort_page_id" />
      </div>

      <div ref="pageCarouselsList">
      <div v-for="page_carousel in page_carousels" :key="page_carousel.id" class="border rounded mb-2">
        <div class="row my-2" v-if="currentEditingPageCarouselId !== page_carousel.id">
          <div class="col-6 d-flex align-items-center ms-2">
            <span class="drag-handle carousel-drag-handle me-2" data-bs-toggle="tooltip" :data-bs-title="t('cohort_page_carousels.drag_to_reorder')" :aria-label="t('cohort_page_carousels.drag_to_reorder')"><i class="bi bi-grip-vertical" aria-hidden="true"></i></span>
            {{ page_carousel.title }}
          </div>
          <div class="col d-flex align-items-center">
            <span class="me-1" v-if="page_carousel.start_date || page_carousel.end_date" data-bs-toggle="tooltip" data-bs-html="true" :data-bs-title="timeTooltip(page_carousel)"><i class="bi bi-calendar-range" :class="timeClass(page_carousel)" aria-hidden="true"></i></span>
          </div>
          <div class="col-auto d-flex align-items-center me-2">
            <div class="btn-toolbar" role="toolbar">
              <div class="btn-group form-btn-group" role="group">
                <button class="btn btn-sm btn-primary" data-bs-toggle="tooltip" :data-bs-title="t('cohort_page_carousels.edit')" :aria-label="t('cohort_page_carousels.edit')" @click="editPageCarousel(page_carousel.id)"><i class="bi bi-pencil-fill" aria-hidden="true"></i></button>
                <button class="btn btn-sm btn-danger" data-bs-toggle="tooltip" :data-bs-title="t('cohort_page_carousels.delete')" :aria-label="t('cohort_page_carousels.delete')" @click="deletePageCarousel(page_carousel.id)"><i class="bi bi-trash3-fill" aria-hidden="true"></i></button>
              </div>
            </div>
          </div>
        </div>
        <PageCarouselForm v-else v-model="currentEditingPageCarouselId" @saved="loadPageCarousels" :cohort_page_id="props.cohort_page_id"/>
      </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .drag-handle {
    cursor: grab;
  }
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
